import isEqual from 'lodash.isequal';
import { useAppSelector } from '@store';
import {
  ButtonPrimary,
  ButtonPseudo,
  PermissionDenied,
  PermissionEnum,
  Popup,
  SidePanel,
  SidePanelType,
} from '@ui/src';
import { MarginDetailsTypes } from '../types';
import useMarginDetails from '../hooks/use-margins-details';
import MarginPanelContent from '../../../../components/common/tariff/margin-panel-content';
import { CustomerStatus } from '../../types';

const MarginDetails = ({
  setPanelIsOpen,
  customerId,
  defaultMargin,
  allMarginsVisible,
  selectedCurrency,
  tariffId,
  panelIsOpen,
  selectedMarginId,
  status,
}: MarginDetailsTypes) => {
  const { customMargin: marginValues, rateSources } = useAppSelector(
    (state) => state.customerTariffs,
  );
  const { activePermissions, currencies } = useAppSelector(
    (state) => state.root,
  );

  const {
    marginIsLoading,
    isFetching,
    control,
    errors,
    amountsList,
    setValue,
    setAmounts,
    marginFeeError,
    setRestoreConfrimationPopupVisible,
    restoreInProgress,
    currencyFromId,
    isDirty,
    initialMarginAmounts,
    amounts,
    setDirtyPopupVisible,
    handleClose,
    globalError,
    createInProgress,
    updateInProgress,
    onFormSubmit,
    createAmountsInProgress,
    marginsIsEqual,
    dirtyPopupVisible,
    onResetMargins,
    restoreConfirmationPopupVisible,
    onDeleteAmount,
  } = useMarginDetails({
    setPanelIsOpen,
    marginValues,
    customerId,
    defaultMargin,
    allMarginsVisible,
    selectedCurrency,
    tariffId,
    panelIsOpen,
    selectedMarginId,
  });

  let titleText: string;

  if (marginIsLoading || isFetching) {
    titleText = 'Loading...';
  } else {
    titleText = `${marginValues?.currencyFrom?.name}-${marginValues?.currencyTo?.name} FX margin`;
  }

  let content;

  if (
    !activePermissions?.includes(
      PermissionEnum.SETTINGS_EXCHANGE_EDIT_MARGINS_ACTION,
    )
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <MarginPanelContent
        control={control}
        errors={errors}
        isLoading={marginIsLoading || isFetching}
        amounts={amountsList}
        setValue={setValue}
        isEditable={activePermissions?.includes(
          PermissionEnum.SETTINGS_EXCHANGE_EDIT_MARGINS_ACTION,
        )}
        isCustom={marginValues?.isCustom}
        setAmounts={setAmounts}
        marginId={selectedMarginId}
        marginFeeError={marginFeeError}
        auditLogAvailable={activePermissions?.includes(
          PermissionEnum.AUDIT_LOG_LIST,
        )}
        onResetMargins={setRestoreConfrimationPopupVisible}
        userMargin={Boolean(
          marginValues?.isCustom && marginValues.organizationId,
        )}
        restoreInProgress={restoreInProgress}
        currencyFromId={currencyFromId}
        status={status}
        onDeleteAmount={onDeleteAmount}
        rateSources={rateSources}
        currencies={currencies}
      />
    );
  }

  return (
    <>
      <SidePanel
        closeOnClickOutside
        isOpen={panelIsOpen}
        type={SidePanelType.Exchange}
        onClose={
          isDirty ||
          (selectedMarginId && !isEqual(initialMarginAmounts, amounts))
            ? () => setDirtyPopupVisible(true)
            : handleClose
        }
        title={titleText}
        sidepanelExpandedKey="customers-margin"
        errorSidepanel={globalError}
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_EXCHANGE_EDIT_MARGINS_ACTION,
            ) &&
              status !== CustomerStatus.CLOSED && (
                <ButtonPrimary
                  text={
                    createInProgress || updateInProgress || restoreInProgress
                      ? 'Loading...'
                      : 'Save'
                  }
                  onClick={onFormSubmit}
                  disabled={
                    createInProgress ||
                    updateInProgress ||
                    createAmountsInProgress ||
                    Boolean(
                      marginsIsEqual &&
                      marginValues?.isCustom &&
                      marginValues.organizationId,
                    ) ||
                    restoreInProgress
                  }
                  bold
                />
              )}

            <ButtonPseudo
              text="Cancel"
              onClick={
                isDirty ||
                (selectedMarginId && !isEqual(initialMarginAmounts, amounts))
                  ? () => setDirtyPopupVisible(true)
                  : handleClose
              }
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <Popup
        title="Are you sure you want to restore to default this margin?"
        onCancelClick={() => setRestoreConfrimationPopupVisible(false)}
        onOkClick={onResetMargins}
        cancelButtonText="Cancel"
        okButtonText="Yes, restore"
        isVisible={restoreConfirmationPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default MarginDetails;
